import React, { useState } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import Chat from './components/Chat';
import RegisterForm from './components/auth/RegisterForm';
import LoginForm from './components/auth/LoginForm';
import appStyles from './App.module.css';
import WelcomePage from './pages/WelcomePage';
import CheckoutReturn from './pages/CheckoutReturn';
import SuccessPage from './pages/SuccessPage';
import './components/auth/LoginRegister.css';
import styles from './App.module.css';
import { useChat } from './context/ChatContext';
import TestChatInput from './pages/TestChatInput';
import SplashPage from './pages/SplashPage';
import AboutPage from './pages/InfoPages/AboutPage';
import HelpPage from './pages/InfoPages/HelpPage';
import PrivacyPage from './pages/InfoPages/PrivacyPage';
import FounderPage from './pages/InfoPages/FounderMessage';
import TermsPage from './pages/InfoPages/TermsPage';
import ContactPage from './pages/InfoPages/ContactPage';
import BurgerMenu from './components/layout/BurgerMenu';
import FaqPage from './pages/InfoPages/FaqPage';
import ResetPassword from './components/auth/ResetPassword';
import ResetConfirm from './components/auth/ResetConfirm';
import Home from './pages/mind-mesch/Home';
import About from './pages/mind-mesch/About';
import Faq from './pages/mind-mesch/Faq';
import Help from './pages/mind-mesch/Help';
import Privacy from './pages/mind-mesch/Privacy';
import Terms from './pages/mind-mesch/Terms';
import PartnerApi from './pages/mind-mesch/PartnerAPI';
import PartnerDocs from './pages/mind-mesch/Docs';
import DocsViewer from './pages/DocsViewer';
import { debugLog } from './utils/logging/debugLog';

debugLog('👀 App.js mounted');

// 🔒 ALPHA GATE SWITCH
const ALPHA_LOCK = true;

// ✅ APPROVED ALPHA TESTERS
const ALPHA_TESTERS = {
  161: {
    name: 'Christopher Ciappa',
    email: 'csciappa@samirac.com',
    notes: 'Founder / panel access',
  },
  164: {
    name: 'odious lumbago',
    email: 'meltronics@gmail.com',
    notes: 'Approved alpha tester',
  },
  165: {
    name: 'Ajay Kumar',
    email: 'k.ajay147@gmail.com',
    notes: 'Approved alpha tester',
  },
  167: {
    name: 'Brian Grant',
    email: 'bjgrant1234@gmail.com',
    notes: 'Approved alpha tester',
  },
  168: {
    name: 'Steve Hunter',
    email: 'steve@hunterta.com',
    notes: 'Approved alpha tester',
  },
  169: {
    name: 'Mark Schroeder',
    email: 'schroedermls@gmail.com',
    notes: 'Approved alpha tester',
  },
  171: {
    name: 'Carole Notto',
    email: 'canotto69@gmail.com',
    notes: 'Approved alpha tester',
  },
};

const ALPHA_TESTER_IDS = Object.keys(ALPHA_TESTERS).map(Number);

const ALPHA_TESTER_EMAILS = Object.values(ALPHA_TESTERS)
  .map((tester) => tester.email?.toLowerCase?.())
  .filter(Boolean);

const isAlphaTester = (user) => {
  if (!user) return false;

  const email = user.email?.toLowerCase?.();

  return (
    ALPHA_TESTER_IDS.includes(user?.id) ||
    (email && ALPHA_TESTER_EMAILS.includes(email))
  );
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useChat();

  if (loading) return <div>Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;

  return typeof children === 'function'
    ? children({ user })
    : children;
};

const App = () => {
  const [theme, setTheme] = useState('light');

  const toggleTheme = () => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  };

  return (
    <Router>
      <div
        className={`${appStyles.container} ${
          theme === 'dark' ? appStyles.darkBackground : appStyles.lightBackground
        }`}
      >
        <div className={appStyles.headerBar}>
          <div className={appStyles.headerContent}>
            <div className={appStyles.headerLeft}>
              <BurgerMenu />
            </div>

            <h1 className={styles.headerTitle}>
              <span className={styles['daisy-dark']}>d</span>
              <span className={styles['daisy-highlight']}>AI</span>
              <span className={styles['daisy-dark']}>sy</span>
            </h1>

            <div className={appStyles.themeToggle}>
              <button onClick={toggleTheme}>
                {theme === 'dark' ? '🌞' : '🌙'}
              </button>
            </div>
          </div>
        </div>

        <Routes>
          <Route
            path="/chat"
            element={
              <ProtectedRoute>
                {({ user }) => {
                  const isTester = isAlphaTester(user);

                  if (ALPHA_LOCK && !isTester) {
                    return <Navigate to="/" replace />;
                  }

                  return <Chat theme={theme} />;
                }}
              </ProtectedRoute>
            }
          />

          <Route path="/test-chat-input" element={<TestChatInput />} />

          <Route path="/" element={<SplashPage theme={theme} />} />

          <Route path="/login" element={<LoginForm />} />
          <Route path="/register" element={<RegisterForm />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/reset-confirm" element={<ResetConfirm />} />

          <Route path="/about" element={<AboutPage theme={theme} />} />
          <Route path="/help" element={<HelpPage theme={theme} />} />
          <Route path="/privacy" element={<PrivacyPage theme={theme} />} />
          <Route path="/founder" element={<FounderPage theme={theme} />} />
          <Route path="/terms" element={<TermsPage theme={theme} />} />
          <Route path="/contact" element={<ContactPage theme={theme} />} />
          <Route path="/faq" element={<FaqPage theme={theme} />} />
          <Route path="/docs" element={<DocsViewer />} />

          <Route path="/mind-mesch" element={<Home />} />
          <Route path="/mind-mesch/about" element={<About />} />
          <Route path="/mind-mesch/faq" element={<Faq />} />
          <Route path="/mind-mesch/help" element={<Help />} />
          <Route path="/mind-mesch/privacy" element={<Privacy />} />
          <Route path="/mind-mesch/terms" element={<Terms />} />
          <Route path="/mind-mesch/partner-api" element={<PartnerApi />} />
          <Route path="/mind-mesch/docs" element={<PartnerDocs />} />

          <Route path="/welcome" element={<WelcomePage />} />
          <Route path="/checkout-return" element={<CheckoutReturn />} />
          <Route path="/success" element={<SuccessPage />} />
        </Routes>

        <p className={appStyles.watermark}>Your AI Chat Pal</p>
      </div>
    </Router>
  );
};

export default App;